import React, { useState, useEffect } from 'react';
import { authService } from '../../services/authService';

interface DashboardData {
  statistics: {
    totalLeads: number;
    newLeadsToday: number;
    completedConversations: number;
    resourceDownloads: number;
    activeSegments: number;
    sentCampaigns: number;
  };
  recentLeads: Array<{
    id: number;
    email: string;
    child_name: string;
    lead_score: number;
    created_at: string;
  }>;
  topSegments: Array<{
    name: string;
    lead_count: number;
  }>;
}

const DashboardStats: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await authService.makeAuthenticatedRequest(
          `${process.env.REACT_APP_API_URL}/admin/dashboard`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch dashboard data');
        }
        
        const dashboardData: DashboardData = await response.json();
        setData(dashboardData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const stats = [
    {
      title: 'Total Leads',
      value: data.statistics.totalLeads,
      icon: '👥',
      color: 'bg-blue-500',
    },
    {
      title: 'New Today',
      value: data.statistics.newLeadsToday,
      icon: '📈',
      color: 'bg-green-500',
    },
    {
      title: 'Completed Conversations',
      value: data.statistics.completedConversations,
      icon: '💬',
      color: 'bg-purple-500',
    },
    {
      title: 'Resource Downloads',
      value: data.statistics.resourceDownloads,
      icon: '📥',
      color: 'bg-orange-500',
    },
    {
      title: 'Active Segments',
      value: data.statistics.activeSegments,
      icon: '🎯',
      color: 'bg-teal-500',
    },
    {
      title: 'Campaigns Sent',
      value: data.statistics.sentCampaigns,
      icon: '📧',
      color: 'bg-pink-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className={`${stat.color} rounded-lg p-3 text-white text-2xl mr-4`}>
                {stat.icon}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Leads</h3>
          {data.recentLeads.length > 0 ? (
            <div className="space-y-3">
              {data.recentLeads.slice(0, 5).map((lead) => (
                <div key={lead.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="font-medium text-gray-900">{lead.child_name || 'Unknown'}</p>
                    <p className="text-sm text-gray-600">{lead.email}</p>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      lead.lead_score >= 70
                        ? 'bg-green-100 text-green-800'
                        : lead.lead_score >= 40
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      Score: {lead.lead_score}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No leads yet</p>
          )}
        </div>

        {/* Top Segments */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Segments</h3>
          {data.topSegments.length > 0 ? (
            <div className="space-y-3">
              {data.topSegments.map((segment, index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-gray-900 capitalize">
                      {segment.name.replace('-', ' ')}
                    </p>
                  </div>
                  <div className="text-sm text-gray-600">
                    {segment.lead_count} leads
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No segments yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;